import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { DefaultOption } from 'src/components/molecules/DropdownSelect';
import { Attributes } from 'src/components/organisms';
import {
  useCoiFilterOptionsQuery,
  useGetOrganisationMetaDataQuery,
  useGetOrganisationQuery,
  useGetOrganisationQuestionnairesQuery,
  useGetReportedCategoriesQuery,
  useGroupsAttributesDataQuery,
  useRelationshipTypeQuery,
} from 'src/graphql';
import { FormCategoryId } from 'src/graphql/formManagement/formCategories';
import { useIsFeatureEnabled, useIsLoading } from 'src/hooks';
import * as Types from '../../../../types';

const COI_QUESTIONNAIRE_QUERY_PARAMS = {
  isActive: true,
  status: 'active',
  formCategoryId: FormCategoryId.CONFLICT_OF_INTEREST,
};

export const useRoutingAttributes = (isCOI = false) => {
  const { data: reportedCategoriesData, isLoading: isCategoriesLoading } =
    useGetReportedCategoriesQuery({ isCOI });
  const { data: attributesData, isLoading: isAttributesLoading } =
    useGroupsAttributesDataQuery();
  const { data: relationshipTypeData, isLoading: isRelationshipTypesLoading } =
    useRelationshipTypeQuery();
  const { data: metaDataTypeData, isLoading: isMetaDataTypesLoading } =
    useGetOrganisationMetaDataQuery();
  const {
    data: organisationData,
    isLoading: isOrganisationHotlineNumbersLoading,
  } = useGetOrganisationQuery();
  const { data: coiFilterOptionsData, isLoading: isCoiFilterOptionsLoading } =
    useCoiFilterOptionsQuery(undefined, { enabled: isCOI });
  const { data: coiQuestionnairesData, isLoading: isCoiQuestionnairesLoading } =
    useGetOrganisationQuestionnairesQuery(
      { queryParams: COI_QUESTIONNAIRE_QUERY_PARAMS },
      { enabled: isCOI },
    );

  const { hotlineNumbers } = organisationData?.organisation ?? {};

  const isRoutingAttributesLoading = useIsLoading([
    isCategoriesLoading,
    isAttributesLoading,
    isRelationshipTypesLoading,
    isMetaDataTypesLoading,
    isOrganisationHotlineNumbersLoading,
    isCOI && isCoiFilterOptionsLoading,
    isCOI && isCoiQuestionnairesLoading,
  ]);

  const vaultTalkEnabled = useIsFeatureEnabled(Types.FeatureFlag.VAULT_TALK);
  const oprEnabled = useIsFeatureEnabled(Types.FeatureFlag.PUBLIC_REPORTING);
  const routingCompanyRelationship = useIsFeatureEnabled(
    Types.FeatureFlag.ROUTING_COMPANY_RELATIONSHIP,
  );
  const relationshipToOrgEnabled = useIsFeatureEnabled(
    Types.FeatureFlag.RELATIONSHIP_TO_ORG,
  );
  const slackEnabled = useIsFeatureEnabled(Types.FeatureFlag.SLACK_REPORTING);
  const appEnabled = useIsFeatureEnabled(Types.FeatureFlag.APP_REPORTING);
  const caseImporterEnabled = useIsFeatureEnabled(
    Types.FeatureFlag.CASE_IMPORTER,
  );
  const isLegacyMode = useSelector(
    (state: Types.State) => state.organisation?.isLegacyMode,
  );

  const submissionModes = useMemo<DefaultOption[]>(() => {
    const appSubmissionMode = appEnabled
      ? [
          {
            label: Types.REPORT_SOURCE['app'],
            value: Types.SUBMISSION_MODE.APP_GO_TOGETHER,
          },
        ]
      : [];
    const vaultTalkSubmissionMode = vaultTalkEnabled
      ? [
          {
            label: Types.REPORT_SOURCE['vault-talk'],
            value: Types.SUBMISSION_MODE.VAULT_TALK,
          },
        ]
      : [];
    const oprSubmissionMode = oprEnabled
      ? [
          {
            label: Types.REPORT_SOURCE['open-reporting'],
            value: Types.SUBMISSION_MODE.OPEN_REPORTING,
          },
        ]
      : [];
    const slackSubmissionMode = slackEnabled
      ? [
          {
            label: Types.REPORT_SOURCE['slack'],
            value: Types.SUBMISSION_MODE.SLACK_GO_AHEAD,
          },
        ]
      : [];
    const importedSubmissionMode = caseImporterEnabled
      ? [
          {
            label: Types.REPORT_SOURCE['imported'],
            value: Types.SUBMISSION_MODE.BULK_IMPORT,
          },
        ]
      : [];
    const ReporterPortalSubmissionMode = !isLegacyMode
      ? [
          {
            label: Types.REPORT_SOURCE['reporter-portal'],
            value: Types.SUBMISSION_MODE.REPORTER_PORTAL,
          },
        ]
      : [];

    const submissionModes: DefaultOption[] = [
      ...appSubmissionMode,
      {
        label: Types.REPORT_SOURCE['manual-case-creation'],
        value: Types.SUBMISSION_MODE.MCC,
      },
      ...vaultTalkSubmissionMode,
      ...oprSubmissionMode,
      ...slackSubmissionMode,
      ...importedSubmissionMode,
      ...ReporterPortalSubmissionMode,
    ];

    return submissionModes;
  }, [
    oprEnabled,
    vaultTalkEnabled,
    slackEnabled,
    appEnabled,
    caseImporterEnabled,
    isLegacyMode,
  ]);

  const attributes = useMemo<Attributes>(() => {
    const anonymous = [
      { label: 'No', value: false },
      { label: 'Yes', value: true },
    ];
    const departments = attributesData?.groupsAttributesData?.department ?? [];
    const sites = attributesData?.groupsAttributesData?.site ?? [];
    const countries = attributesData?.groupsAttributesData?.country ?? [];
    const teams = attributesData?.groupsAttributesData?.team ?? [];
    const systemRoles = attributesData?.groupsAttributesData?.systemRoles ?? [];
    const reportedCategories =
      reportedCategoriesData?.getReportedCategories ?? [];
    const relationshipTypeNames =
      relationshipTypeData?.getRelationshipType.map((type) => ({
        label: type.name,
        value: type.id,
      })) ?? [];
    const divisionsNames =
      metaDataTypeData?.getOrganisationMetaData?.division?.map((option) => ({
        label: option,
        value: option,
      })) ?? [];
    const organizationNames =
      metaDataTypeData?.getOrganisationMetaData?.organization?.map(
        (option) => ({
          label: option,
          value: option,
        }),
      ) ?? [];
    const orgHotlineNumbers =
      hotlineNumbers?.map((option) => ({
        label: option.phoneNumber,
        value: option.phoneNumber,
      })) ?? [];
    const coiCategory =
      coiFilterOptionsData?.coiFilterOptions?.categories ?? [];
    const questionnaireTitles = (
      coiQuestionnairesData?.organisationQuestionnaires ?? []
    ).reduce<DefaultOption[]>((options, questionnaire) => {
      const id = questionnaire.id;
      const title = questionnaire.publicTitle;
      if (id && title && !options.some((option) => option.value === id)) {
        options.push({ label: title, value: id });
      }
      return options;
    }, []);

    return {
      department: departments.map((department) => ({
        label: department,
        value: department,
      })),
      site: sites.map((site) => ({ label: site, value: site })),
      country: countries.map((country) => ({ label: country, value: country })),
      team: teams.map((team) => ({
        label: team.name,
        value: team.id,
      })),
      systemRoles: systemRoles.map((systemRole) => ({
        label: systemRole.name,
        value: systemRole.id,
      })),
      submissionMode: submissionModes,
      anonymous,
      reportedCategory: reportedCategories.map((category) => ({
        label: category.name,
        value: category.id,
      })),
      ...(oprEnabled ||
      vaultTalkEnabled ||
      routingCompanyRelationship ||
      relationshipToOrgEnabled
        ? { relationshipToCompany: relationshipTypeNames }
        : {}),
      ...(routingCompanyRelationship
        ? {
            ['userMetaData.organization']: organizationNames,
            ['userMetaData.division']: divisionsNames,
          }
        : {}),
      ...(vaultTalkEnabled
        ? {
            ['Call.organisationHotlineNumber']: orgHotlineNumbers,
          }
        : {}),
      coiCategory,
      questionnaireId: questionnaireTitles,
    };
  }, [
    attributesData?.groupsAttributesData,
    relationshipTypeData?.getRelationshipType,
    submissionModes,
    reportedCategoriesData,
    oprEnabled,
    vaultTalkEnabled,
    routingCompanyRelationship,
    metaDataTypeData?.getOrganisationMetaData,
    hotlineNumbers,
    relationshipToOrgEnabled,
    coiFilterOptionsData?.coiFilterOptions,
    coiQuestionnairesData?.organisationQuestionnaires,
  ]);

  return {
    attributes,
    isRoutingAttributesLoading,
  };
};
